import type { Hex } from 'viem';

/**
 * Normalizes VITE_RELAYER_URL so common misconfigurations don't silently
 * break the relayer connection (which surfaces as "Relayer offline"):
 * - Missing protocol (`host.com`) would be treated as a relative URL by fetch.
 * - A trailing slash turns `${URL}/health` into `//health`, which the worker's
 *   exact `path === '/health'` match rejects with a 404.
 */
function normalizeRelayerUrl(raw: string | undefined): string {
  let url = (raw || '').trim();
  if (!url) return '';
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  return url.replace(/\/+$/, '');
}

const RELAYER_URL = normalizeRelayerUrl(import.meta.env.VITE_RELAYER_URL);

export interface RelayerStatus {
  address: string;
  balance: string;
  balanceEth: string;
  lowBalance: boolean;
  threshold: string;
}

export interface RelayerResponse {
  success: boolean;
  txHash?: string;
  error?: string;
}

/**
 * Checks if the relayer is configured
 */
export function isRelayerConfigured(): boolean {
  return !!RELAYER_URL;
}

/**
 * Gets the relayer status including balance
 */
export async function getRelayerStatus(): Promise<RelayerStatus | null> {
  if (!RELAYER_URL) return null;

  try {
    const response = await fetch(`${RELAYER_URL}/health`);
    if (!response.ok) {
      throw new Error('Relayer health check failed');
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to get relayer status:', error);
    return null;
  }
}

/**
 * Submits a recordSession transaction via the relayer
 */
export async function relayRecordSession(params: {
  cohortTitle: string;        // plaintext
  participantNames: string[]; // plaintext
  importance: number;
  points: bigint[];
  v: number;
  r: Hex;
  s: Hex;
}): Promise<RelayerResponse> {
  if (!RELAYER_URL) {
    return { success: false, error: 'Relayer not configured' };
  }

  try {
    const response = await fetch(`${RELAYER_URL}/recordSession`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cohortTitle: params.cohortTitle,
        participantNames: params.participantNames,
        importance: params.importance,
        points: params.points.map(p => p.toString()),
        v: params.v,
        r: params.r,
        s: params.s,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Unknown error' };
    }

    return { success: true, txHash: data.txHash };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Submits a finalize transaction via the relayer
 */
export async function relayFinalize(params: {
  cohortId: Hex;
  v: number;
  r: Hex;
  s: Hex;
}): Promise<RelayerResponse> {
  if (!RELAYER_URL) {
    return { success: false, error: 'Relayer not configured' };
  }

  try {
    const response = await fetch(`${RELAYER_URL}/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cohortId: params.cohortId,
        v: params.v,
        r: params.r,
        s: params.s,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Unknown error' };
    }

    return { success: true, txHash: data.txHash };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Submits an invalidateSession transaction via the relayer
 */
export async function relayInvalidateSession(params: {
  cohortId: Hex;
  sessionId: bigint;
  v: number;
  r: Hex;
  s: Hex;
}): Promise<RelayerResponse> {
  if (!RELAYER_URL) {
    return { success: false, error: 'Relayer not configured' };
  }

  try {
    const response = await fetch(`${RELAYER_URL}/invalidateSession`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cohortId: params.cohortId,
        sessionId: params.sessionId.toString(),
        v: params.v,
        r: params.r,
        s: params.s,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Unknown error' };
    }

    return { success: true, txHash: data.txHash };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Submits an addSigner transaction via the relayer
 */
export async function relayAddSigner(params: {
  cohortId: Hex;
  newSigner: Hex;
  v: number;
  r: Hex;
  s: Hex;
}): Promise<RelayerResponse> {
  if (!RELAYER_URL) {
    return { success: false, error: 'Relayer not configured' };
  }

  try {
    const response = await fetch(`${RELAYER_URL}/addSigner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cohortId: params.cohortId,
        newSigner: params.newSigner,
        v: params.v,
        r: params.r,
        s: params.s,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Unknown error' };
    }

    return { success: true, txHash: data.txHash };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Submits a removeSigner transaction via the relayer
 */
export async function relayRemoveSigner(params: {
  cohortId: Hex;
  signerToRemove: Hex;
  v: number;
  r: Hex;
  s: Hex;
}): Promise<RelayerResponse> {
  if (!RELAYER_URL) {
    return { success: false, error: 'Relayer not configured' };
  }

  try {
    const response = await fetch(`${RELAYER_URL}/removeSigner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cohortId: params.cohortId,
        signerToRemove: params.signerToRemove,
        v: params.v,
        r: params.r,
        s: params.s,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Unknown error' };
    }

    return { success: true, txHash: data.txHash };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Submits a requestSigner transaction via the relayer
 * This allows someone to request to be added as a signer without needing gas
 */
export async function relayRequestSigner(params: {
  cohortId: Hex;
  requester: Hex;
}): Promise<RelayerResponse> {
  if (!RELAYER_URL) {
    return { success: false, error: 'Relayer not configured' };
  }

  try {
    const response = await fetch(`${RELAYER_URL}/requestSigner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cohortId: params.cohortId,
        requester: params.requester,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Unknown error' };
    }

    return { success: true, txHash: data.txHash };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}
